import { useEffect, useRef } from 'react';
import { Alert, Box, Chip, Skeleton, Stack, alpha } from '@mui/material';
import { MyLocation, Place, Flag } from '@mui/icons-material';
import { DISPATCH } from '../dispatcher/dispatchTheme';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { GOOGLE_MAP_ID, addMapPin, clearMapOverlay, type MapOverlay } from '../../utils/googleMapPins';
import { isValidMapCenter, useMapCenter } from '../../utils/mapCenter';

export interface RouteMapMarker {
  id: string;
  lat: number;
  lng: number;
  label: string;
  color: string;
  kind: 'current' | 'pickup' | 'dropoff' | 'other';
}

interface DispatchRouteMapProps {
  markers?: RouteMapMarker[];
  route?: { lat: number; lng: number }[];
  heatMapPoints?: { lat: number; lng: number; weight: number }[];
  showHeatMap?: boolean;
  center?: { lat: number; lng: number };
  zoom?: number;
  height?: number | string;
  activePin?: 'pickup' | 'dropoff';
  onMapClick?: (lat: number, lng: number) => void;
  chrome?: boolean;
}

type MapLike = {
  setCenter: (c: unknown) => void;
  setZoom: (z: number) => void;
  fitBounds: (b: unknown, o?: unknown) => void;
};

type GMaps = {
  Map: new (...args: unknown[]) => MapLike;
  Circle: new (...args: unknown[]) => MapOverlay;
  Polyline: new (...args: unknown[]) => MapOverlay;
  LatLngBounds: new () => { extend: (p: unknown) => void };
  event: {
    addListener: (t: unknown, e: string, fn: (ev: { latLng?: { lat: () => number; lng: () => number } }) => void) => unknown;
    removeListener: (l: unknown) => void;
    clearInstanceListeners: (t: unknown) => void;
  };
};

export default function DispatchRouteMap({
  markers = [],
  route = [],
  heatMapPoints = [],
  showHeatMap = false,
  center,
  zoom = 13,
  height = 280,
  activePin = 'pickup',
  onMapClick,
  chrome = true,
}: DispatchRouteMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<MapLike | null>(null);
  const overlaysRef = useRef<MapOverlay[]>([]);
  const clickListenerRef = useRef<unknown>(null);
  const { ready, error, hasKey } = useGoogleMaps();
  const preferred = isValidMapCenter(center) ? center : (markers[0] || heatMapPoints[0] || route[0] || undefined);
  const { center: resolvedCenter, ready: centerReady } = useMapCenter(preferred);

  useEffect(() => {
    if (!ready || !centerReady || !resolvedCenter || !mapRef.current) return;
    const g = (window as unknown as { google: { maps: GMaps } }).google;

    if (!mapInstance.current) {
      mapInstance.current = new g.maps.Map(mapRef.current, {
        center: resolvedCenter,
        zoom,
        mapId: GOOGLE_MAP_ID,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'greedy',
      });
    }

    const map = mapInstance.current;
    overlaysRef.current.forEach((layer) => clearMapOverlay(layer));
    overlaysRef.current = [];

    const bounds = new g.maps.LatLngBounds();
    let hasBounds = false;

    if (route.length > 1) {
      overlaysRef.current.push(new g.maps.Polyline({
        map,
        path: route,
        strokeColor: DISPATCH.primary,
        strokeOpacity: 0.75,
        strokeWeight: 4,
      }));
      route.forEach((p) => { bounds.extend(p); hasBounds = true; });
    }

    if (showHeatMap && heatMapPoints.length > 0) {
      const maxWeight = Math.max(...heatMapPoints.map((p) => p.weight), 1);
      heatMapPoints.forEach((p) => {
        const intensity = p.weight / maxWeight;
        overlaysRef.current.push(new g.maps.Circle({
          map,
          center: { lat: p.lat, lng: p.lng },
          radius: 400 + intensity * 600,
          fillColor: '#ec4899',
          fillOpacity: 0.15 + intensity * 0.35,
          strokeColor: '#db2777',
          strokeWeight: 1,
          strokeOpacity: 0.5,
        }));
        // Heat alone should not pull camera away from pickup/drop focus.
        if (!markers.some((m) => m.kind === 'pickup' || m.kind === 'dropoff') && route.length < 2) {
          bounds.extend({ lat: p.lat, lng: p.lng });
          hasBounds = true;
        }
      });
    }

    markers.forEach((m) => {
      overlaysRef.current.push(addMapPin(map, {
        lat: m.lat,
        lng: m.lng,
        title: m.label,
        color: m.color,
        kind: m.kind,
      }));
    });

    // Prefer pickup / drop focus when the dispatcher enters addresses.
    const focusPins = markers.filter((m) => m.kind === 'pickup' || m.kind === 'dropoff');
    const focusForBounds = focusPins.length > 0
      ? focusPins
      : markers.filter((m) => m.kind !== 'current');

    if (focusPins.length > 1 || route.length > 1) {
      focusPins.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
      if (route.length > 1) route.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, 64);
    } else if (focusPins.length === 1) {
      const pin = (activePin && focusPins.find((m) => m.kind === activePin)) || focusPins[0];
      map.setCenter({ lat: pin.lat, lng: pin.lng });
      map.setZoom(16);
    } else if (focusForBounds.length === 1) {
      map.setCenter({ lat: focusForBounds[0].lat, lng: focusForBounds[0].lng });
      map.setZoom(15);
    } else if (focusForBounds.length > 1) {
      focusForBounds.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
      map.fitBounds(bounds, 48);
    } else if (!hasBounds) {
      map.setCenter(resolvedCenter);
      map.setZoom(zoom);
    }

    if (clickListenerRef.current) {
      g.maps.event.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }
    if (onMapClick) {
      clickListenerRef.current = g.maps.event.addListener(map, 'click', (ev) => {
        const ll = ev.latLng;
        if (!ll) return;
        onMapClick(ll.lat(), ll.lng());
      });
    }
  }, [ready, centerReady, resolvedCenter?.lat, resolvedCenter?.lng, markers, route, heatMapPoints, showHeatMap, center?.lat, center?.lng, zoom, onMapClick, activePin]);

  if (!hasKey) {
    return <Alert severity="warning">Google Maps key missing. Set VITE_GOOGLE_MAPS_API_KEY in frontend/.env.local</Alert>;
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!ready || !centerReady) {
    return <Skeleton variant="rounded" height={height} sx={{ borderRadius: 3, minHeight: height === '100%' ? 280 : undefined }} />;
  }

  const resolvedHeight = height === '100%' ? '100%' : height;

  return (
    <Box sx={{ position: 'relative', height: height === '100%' ? '100%' : undefined }}>
      <Box
        ref={mapRef}
        data-map-engine="google-maps"
        sx={{
          width: '100%',
          height: resolvedHeight,
          minHeight: height === '100%' ? 0 : 220,
          borderRadius: chrome ? `${DISPATCH.radiusSm}px` : 0,
          overflow: 'hidden',
          border: chrome ? `1px solid ${DISPATCH.border}` : 'none',
          boxShadow: chrome ? DISPATCH.shadow : 'none',
        }}
      />
      {chrome && (
        <>
          <Stack direction="row" spacing={0.75} sx={{ position: 'absolute', top: 10, left: 10, zIndex: 500 }}>
            <Chip size="small" icon={<Place sx={{ fontSize: '14px !important', color: '#22c55e !important' }} />} label="Pickup" sx={{ fontWeight: 700, bgcolor: alpha('#fff', 0.94), backdropFilter: 'blur(6px)' }} />
            <Chip size="small" icon={<Flag sx={{ fontSize: '14px !important', color: '#ef4444 !important' }} />} label="Drop" sx={{ fontWeight: 700, bgcolor: alpha('#fff', 0.94), backdropFilter: 'blur(6px)' }} />
            <Chip size="small" icon={<MyLocation sx={{ fontSize: '14px !important', color: '#2563eb !important' }} />} label="You" sx={{ fontWeight: 700, bgcolor: alpha('#fff', 0.94), backdropFilter: 'blur(6px)' }} />
          </Stack>
          <Chip
            size="small"
            label={`Pin: ${activePin === 'pickup' ? 'Pickup' : 'Drop-off'}`}
            sx={{ position: 'absolute', bottom: 10, right: 10, zIndex: 500, fontWeight: 800, bgcolor: alpha(DISPATCH.primary, 0.92), color: '#fff' }}
          />
        </>
      )}
    </Box>
  );
}
